/**
 * @file order.service.ts
 */
import { Injectable } from '@angular/core';
import { HttpService } from '../../core/services/http/http.service';
import { catchError, defer, map, Observable, of, take } from 'rxjs';
import { OrdersResponse } from '../../interfaces/orders-response.interface';
import { CreateOrdersResponse } from '../../interfaces/create-order-response.interface';
import { PaymentCardsResponse } from '../../interfaces/payment-cards.interface';
import { PrescriptionsResponse } from '../../interfaces/prescriptions-response.interface';
import { ShippingAddressesResponse } from '../../interfaces/shipping-addresses-response.interface';
import { MailOrder } from '../../interfaces/mail-order.interface';
import { PrescriptionFill } from '../../interfaces/prescription-fill.interface';
import { RequestOrder } from '../../interfaces/request/request-order.interface';
import { UserService } from '../user/user.service';
import { APIConstants } from '../services.constants';
import { ShippingAddress } from '../../interfaces/shipping-address.interface';
import { RequestOptions } from '../../interfaces/request-options.interface';
import { CreateCreditCardRequest, CreditCard } from '../../interfaces/credit-card.interface';
import { HttpHeaders } from '@angular/common/http';
import { Prescription, PrescriptionAutoRefill } from 'src/app/interfaces/prescription.interface';
import { CmsService } from '../cms/cms.service';

@Injectable({
  providedIn: 'root'
})
export class OrderService {

  constructor(
    private readonly httpService: HttpService,
    private readonly userService: UserService,
    private cmsService: CmsService
  ) { }

  public getOrdersByDateRange(): Observable<OrdersResponse> {
    return this.httpService.get<OrdersResponse>(`${APIConstants.MAIL_ORDER_REFILLS_SERVICES.GET_ORDERS}?cardId=${this.userService.eligibilityVerificationResponse?.cardId}&clientId=${this.userService.eligibilityVerificationResponse?.clientId}&personCode=${this.userService.eligibilityVerificationResponse?.personCode}`);
  }

  public createOrders(request: RequestOrder): Observable<CreateOrdersResponse> {
    return this.httpService.post<CreateOrdersResponse>(APIConstants.MAIL_ORDER_REFILLS_SERVICES.POST_CREATE_ORDERS, request);
  }

  public getPaymentCards(): Observable<PaymentCardsResponse> {
    return this.httpService.get<PaymentCardsResponse>(`${APIConstants.MAIL_ORDER_REFILLS_SERVICES.GET_PAYMENT_CARDS}?cardId=${this.userService.eligibilityVerificationResponse?.cardId}&clientId=${this.userService.eligibilityVerificationResponse?.clientId}&personCode=${this.userService.eligibilityVerificationResponse?.personCode}`);
  }

  public getPrescriptions(): Observable<PrescriptionsResponse> {
    return this.httpService.get<PrescriptionsResponse>(`${APIConstants.MAIL_ORDER_REFILLS_SERVICES.GET_PRESCRIPTIONS}?cardId=${this.userService.eligibilityVerificationResponse?.cardId}&clientId=${this.userService.eligibilityVerificationResponse?.clientId}&personCode=${this.userService.eligibilityVerificationResponse?.personCode}`);
  }

  public getShippingAddresses(): Observable<ShippingAddressesResponse> {
    return this.httpService.get<ShippingAddressesResponse>(`${APIConstants.MAIL_ORDER_REFILLS_SERVICES.GET_SHIPPING_ADDRESS}?cardId=${this.userService.eligibilityVerificationResponse?.cardId}&clientId=${this.userService.eligibilityVerificationResponse?.clientId}&personCode=${this.userService.eligibilityVerificationResponse?.personCode}`);
  }

  public createCreditCard(creditCard: CreateCreditCardRequest): Observable<void> {
    return this.httpService.post<void>(`${APIConstants.MAIL_ORDER_REFILLS_SERVICES.GET_PAYMENT_CARDS}`,
      creditCard, { headers: new HttpHeaders({ 'Content-Type': 'application/json', }), responseType: 'text' } as RequestOptions);
  }

  public updateCreditCard(creditCard: CreditCard): Observable<void> {
    return this.httpService.put<void>(`${APIConstants.MAIL_ORDER_REFILLS_SERVICES.GET_PAYMENT_CARDS}?cardId=${this.userService.eligibilityVerificationResponse?.cardId}&clientId=${this.userService.eligibilityVerificationResponse?.clientId}&personCode=${this.userService.eligibilityVerificationResponse?.personCode}`,
      creditCard, { headers: new HttpHeaders({ 'Content-Type': 'application/json', }), responseType: 'text' } as RequestOptions);
  }

  public addShippingAddress(shippingAddress: ShippingAddress): Observable<ShippingAddress> {
    return this.httpService.post<ShippingAddress>(`${APIConstants.MAIL_ORDER_REFILLS_SERVICES.GET_SHIPPING_ADDRESS}?cardId=${this.userService.eligibilityVerificationResponse?.cardId}&clientId=${this.userService.eligibilityVerificationResponse?.clientId}&personCode=${this.userService.eligibilityVerificationResponse?.personCode}`, shippingAddress);
  }

  public deleteShippingAddress(shippingAddress: ShippingAddress): Observable<void> {
    return this.httpService.delete<void>(`${APIConstants.MAIL_ORDER_REFILLS_SERVICES.GET_SHIPPING_ADDRESS}?cardId=${this.userService.eligibilityVerificationResponse?.cardId}&clientId=${this.userService.eligibilityVerificationResponse?.clientId}&personCode=${this.userService.eligibilityVerificationResponse?.personCode}`, { headers: new HttpHeaders({ 'Content-Type': 'application/json', }), body: shippingAddress, responseType: 'text' } as RequestOptions);
  }

  public deleteCreditCard(creditCard: CreditCard): Observable<void> {
    return this.httpService.delete<void>(`${APIConstants.MAIL_ORDER_REFILLS_SERVICES.GET_PAYMENT_CARDS}?cardId=${this.userService.eligibilityVerificationResponse?.cardId}&clientId=${this.userService.eligibilityVerificationResponse?.clientId}&personCode=${this.userService.eligibilityVerificationResponse?.personCode}`, { headers: new HttpHeaders({ 'Content-Type': 'application/json', }), body: creditCard, responseType: 'text' } as RequestOptions);
  }
  public setAutoRefillForPrescription(reqBody: PrescriptionAutoRefill): Observable<string> {
    return this.httpService.post<string>(`${APIConstants.MAIL_ORDER_REFILLS_SERVICES.SET_AUTO_REFILL}`,
      reqBody, { headers: new HttpHeaders({ 'Content-Type': 'application/json', }), responseType: 'text' } as RequestOptions);
  }

  public mapOrderAndPrescription(orders: MailOrder<Array<PrescriptionFill>>[]): MailOrder<PrescriptionFill>[] {
    const newFlatOrders: MailOrder<PrescriptionFill>[] = [];
    orders.forEach(order => {
      if (order.renewalRequest) {
        order.prescriptionFills = [order.renewalRequest];
      }
      order.prescriptionFills = order.prescriptionFills ? order.prescriptionFills : [];
      order.prescriptionFills.forEach((prescriptionFill: any) => {
        const newObj = JSON.parse(JSON.stringify(order));
        newObj.prescriptionFills = JSON.parse(JSON.stringify(prescriptionFill));
        newFlatOrders.push(newObj);
      });
    });
    return newFlatOrders;
  }

  public checkForAutoRefillSettings(response: PrescriptionsResponse, isChargeShipEnabled: boolean) {
    response.prescriptions = response.prescriptions.map((item) => {
      if (item.drugDeaClass == '2' || (+item.daysSupply < 14) || !this.userService.isOrionUser()) {
        item.autoRefillReminderAllowed = false;
      } else {
        item.autoRefillReminderAllowed = true;
      }

      if (!isChargeShipEnabled || item.drugDeaClass == '2' || item.drugDeaClass == '3' || item.drugDeaClass == '4' || item.drugDeaClass == '5' || item.specialtyIndicator == 'YES' || (+item.daysSupply < 14) || !this.userService.isOrionUser()) {
        item.autoRefillChargeShipAllowed = false;
      } else {
        item.autoRefillChargeShipAllowed = true;
      }
      return item;
    });
    return response;
  }


  public showChargeShipByClientId(memberClientId: string): Observable<boolean> {
    return this.cmsService.loadConfigurations(memberClientId).pipe(
      take(1),
      map((cmsConfig) => cmsConfig.clientSpecificConfig.enableAutoChargeShip)
    );
  }

  /**
   * Method is used enable the auto refill charge & ship option to Orion members whose client IDs are configured in wordpress
   * @returns
   */
  public isEnableChargeShip(clientId: string): Observable<boolean> {
    try {
      // get logged in member
      const clientId = this.userService.eligibilityVerificationResponse?.clientId;
      const cardId = this.userService.eligibilityVerificationResponse?.cardId;
      const personCode = this.userService.eligibilityVerificationResponse?.personCode;

      if (!clientId || !cardId || !personCode) return of(false);

      return defer(() => {
        if (this.userService.isOrionUser()) {
          return this.showChargeShipByClientId(clientId).pipe(map((show) => {
            return show ? true : false;
          }));
        } else {
          return of(false);
        }
      });
    } catch (error) {
      return of(false);
    }
  }

  public prepareAutoRefillRequest(prescriptionList: Prescription[], prescription: Prescription, type: 'Reminder' | 'Charge_Ship', value: 'YES' | 'NO'): PrescriptionAutoRefill {
    const getPersonCode = prescriptionList.find(o => o.rxNumber == prescription.rxNumber)?.patientPersonCode;
    const clientId = this.userService.eligibilityVerificationResponse?.clientId ? this.userService.eligibilityVerificationResponse?.clientId : '';
    const cardId = this.userService.eligibilityVerificationResponse?.cardId ? this.userService.eligibilityVerificationResponse?.cardId : '';

    let data: PrescriptionAutoRefill = {
      clientId: clientId,
      cardId: cardId,
      personCode: getPersonCode ? getPersonCode : '',
      rxNumber: prescription.rxNumber,
      autoRefillReminder: prescription.autoRefillReminder,
      autoRefillChargeShip: prescription.autoRefillChargeShip,
      isChargeAndShipAttested: prescription.isChargeAndShipAttested,
      attestationDate: prescription.latestAttestationDate,
    }

    if (type === 'Reminder') {
      data.autoRefillReminder = value;
      data.isChargeAndShipAttested = false;
      data.attestationDate = null;
      value == 'YES' ? (data.autoRefillChargeShip = 'NO') : null;

    } else if (type === 'Charge_Ship') {

      data.autoRefillChargeShip = value;
      if (value === 'YES') {
        data.isChargeAndShipAttested = true;
        data.attestationDate = this.formatDateRFC3339(new Date());
        data.autoRefillReminder = 'NO';
      } else {
        data.isChargeAndShipAttested = false;
        data.attestationDate = null;
      }
    }
    return data;
  }

  public isAutoRefillChargeShipEnable(): Observable<boolean> {
    const clientId = this.userService.eligibilityVerificationResponse?.clientId;
    if (clientId) {
      return this.isEnableChargeShip(clientId).pipe(take(1), catchError(() => {
        return of(false);
      }));
    } else {
      return of(false)
    }
  }

  public formatDateRFC3339(date: Date): string {
    return `${date.getFullYear()}-${this.leftPadDateString(date.getMonth() + 1)}-${this.leftPadDateString(date.getDate())}`;
  }

  public leftPadDateString(n: number): string {
    return `0000${n}`.slice(-2);
  }

  public enableMailOrderBanner(clientId: string): Observable<boolean> {
    return this.cmsService.loadConfigurations(clientId).pipe(
      take(1),
      map((cmsConfig) => {
        return cmsConfig.clientSpecificConfig.enableBannerMessage.mailOrderScreen
      })
    );
  }

  public enableOrderCheckoutBanner(clientId: string): Observable<boolean> {
    return this.cmsService.loadConfigurations(clientId).pipe(
      take(1),
      map((cmsConfig) => {
        return cmsConfig.clientSpecificConfig.enableBannerMessage.orderCheckoutScreen
      })
    );
  }
}


